import type { TutorTasksIndexData } from '../model/tutor-tasks/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { getTutorTasksFilterParams } from '../lib/tutor-tasks-query'
import { useDisciplineTasksStore } from '../model/tasks/store'

interface DisciplineTasksApiOptions {
  disciplineId: string | number
}

/** Filters and pages change faster than the list answers, so only the latest request may land. */
let activeRequest: AbortController | null = null

export const abortDisciplineTasksApi = (): void => {
  activeRequest?.abort()
  activeRequest = null
  useDisciplineTasksStore().setLoading(false)
}

/** `GET /admin-api/tutor-tasks?discipline={id}` — the global task list narrowed to the discipline. */
export const useDisciplineTasksApi = async (options: DisciplineTasksApiOptions): Promise<TutorTasksIndexData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useDisciplineTasksStore()

  const url = useAdminRoutes('admin.tutor-tasks.data', pick({
    discipline: options.disciplineId,
    ...getTutorTasksFilterParams(store.getFilters),
    per_page: store.getPerPage,
    page: store.getCurrentPage,
  }))

  activeRequest?.abort()
  const request = new AbortController()
  activeRequest = request

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url, { signal: request.signal })

    if (request.signal.aborted) return null

    const indexData: TutorTasksIndexData | null = data.data || null

    if (indexData) {
      store.setTasks(indexData.tasks)
      store.setMeta(indexData.meta)
    }

    return indexData
  } catch (err: unknown) {
    if (request.signal.aborted) return null

    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    if (activeRequest === request) {
      activeRequest = null
      store.setLoading(false)
    }
  }
}
